import { Fragment, useEffect, useState, type MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { navigateAwayFromWorkOrderDetail } from '@/lib/workOrderNavigation';
import { darkSurfaces } from '@/utils/themeBuilder';
import {
  filterVisibleNavEntries,
  isNavGroup,
  isNavItemVisible,
  isNavPathActive,
  navItems,
  navigationEntries,
  settingsNavItem,
  type NavGroup,
  type NavItem,
} from './navigationModel';

const NAV_ICON_SLOT = 40;
const NESTED_NAV_ICON_SLOT = 32;

// Group headers look the same open or closed, and unlike an item row (#3544).
const groupLabelTypographyProps = {
  noWrap: true,
  lineHeight: 2.2,
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'text.secondary',
} as const;
const CANDIDATE_PATHS = navItems.map((item) => item.path);

export type NavListVariant = 'rail' | 'drawer';

export interface NavListProps {
  variant: NavListVariant;
  collapsed?: boolean;
  includeSettings?: boolean;
  onItemClick?: () => void;
}

function useVisibleNav() {
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const { inspectionsEnabled } = useTenantSettings();
  const modules = { inspectionsEnabled };
  const entries = filterVisibleNavEntries(navigationEntries, hasAnyPermission, modules);
  const settingsVisible = isNavItemVisible(settingsNavItem, hasAnyPermission, modules);
  return { entries, settingsVisible };
}

function itemIsActive(item: NavItem, pathname: string, search: string): boolean {
  return isNavPathActive(item.path, pathname, search, CANDIDATE_PATHS);
}

function groupIsActive(group: NavGroup, pathname: string, search: string): boolean {
  return group.items.some((item) => itemIsActive(item, pathname, search));
}

export function NavItemRow({
  item,
  variant,
  collapsed = false,
  nested = false,
  onItemClick,
}: {
  item: NavItem;
  variant: NavListVariant;
  collapsed?: boolean;
  /** Item inside an open group: smaller icon and padding so the indent costs no label width. */
  nested?: boolean;
  onItemClick?: () => void;
}) {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const navigate = useNavigate();
  const isCollapsed = variant === 'rail' && collapsed;
  const active = itemIsActive(item, location.pathname, location.search);
  const Icon = item.icon;
  const label = t(item.labelKey);
  const buttonSx = navButtonSx(active, isCollapsed, variant);

  const button = (
    <ListItemButton
      onClick={() => {
        navigateAwayFromWorkOrderDetail(location.pathname, item.path, navigate);
        onItemClick?.();
      }}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      sx={
        nested
          ? {
              ...buttonSx,
              px: 1.5,
              '& .MuiListItemIcon-root': {
                ...buttonSx['& .MuiListItemIcon-root'],
                minWidth: NESTED_NAV_ICON_SLOT,
              },
            }
          : buttonSx
      }
    >
      <ListItemIcon>
        <Icon fontSize={nested ? 'small' : 'medium'} />
      </ListItemIcon>
      {!isCollapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            noWrap: nested,
            fontSize: '0.875rem',
            fontWeight: active ? 600 : 400,
            sx: {
              color: active
                ? variant === 'rail'
                  ? 'primary.contrastText'
                  : 'inherit'
                : 'text.primary',
            },
          }}
        />
      )}
    </ListItemButton>
  );

  return (
    <ListItem disablePadding>
      {isCollapsed ? (
        <Tooltip title={label} placement="right">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
}

export function NavList({
  variant,
  collapsed = false,
  includeSettings = false,
  onItemClick,
}: NavListProps) {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const navigate = useNavigate();
  const { entries, settingsVisible } = useVisibleNav();
  const isCollapsed = variant === 'rail' && collapsed;
  const [flyout, setFlyout] = useState<{ groupId: string; anchor: HTMLElement } | null>(null);
  const expandedNavGroups = useUIStore((s) => s.expandedNavGroups);
  const toggleNavGroup = useUIStore((s) => s.toggleNavGroup);
  const expandNavGroup = useUIStore((s) => s.expandNavGroup);

  useEffect(() => {
    if (!isCollapsed) {
      setFlyout(null);
    }
  }, [isCollapsed]);

  // Groups start closed, but the group holding the current page opens so the
  // active item never hides behind a closed header (#3534).
  const activeGroupId = entries.find(
    (entry): entry is NavGroup =>
      isNavGroup(entry) && groupIsActive(entry, location.pathname, location.search),
  )?.id;
  useEffect(() => {
    if (activeGroupId) {
      expandNavGroup(activeGroupId);
    }
  }, [activeGroupId, expandNavGroup]);

  const openFlyout = (groupId: string, event: MouseEvent<HTMLElement>) => {
    const anchor = event.currentTarget;
    setFlyout((current) =>
      current?.groupId === groupId ? null : { groupId, anchor },
    );
  };

  const closeFlyout = () => setFlyout(null);

  const openGroup =
    isCollapsed && flyout
      ? entries.find((entry): entry is NavGroup => isNavGroup(entry) && entry.id === flyout.groupId)
      : undefined;

  return (
    <>
      {entries.map((entry, index) => {
        const previous = index > 0 ? entries[index - 1] : undefined;
        // A divider separates every switch between plain items and groups, so the
        // trailing items after the last group stand apart from it (#3182).
        const showDivider =
          previous !== undefined && isNavGroup(entry) !== isNavGroup(previous);

        if (isNavGroup(entry)) {
          const groupActive = groupIsActive(entry, location.pathname, location.search);
          const GroupIcon = entry.icon;
          const groupLabel = t(entry.labelKey);
          const menuOpen = flyout?.groupId === entry.id;
          const groupExpanded = expandedNavGroups.includes(entry.id);
          const groupListId = `nav-group-${variant}-${entry.id}`;

          return (
            <Fragment key={entry.id}>
              {showDivider && <Divider component="li" sx={{ my: 1 }} />}
              {isCollapsed ? (
                <ListItem disablePadding>
                  <Tooltip title={groupLabel} placement="right">
                    <ListItemButton
                      onClick={(event) => openFlyout(entry.id, event)}
                      aria-label={groupLabel}
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                      aria-controls={menuOpen ? `nav-group-menu-${entry.id}` : undefined}
                      aria-current={groupActive ? 'page' : undefined}
                      sx={navButtonSx(groupActive, true, variant)}
                    >
                      <ListItemIcon>
                        <GroupIcon />
                      </ListItemIcon>
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ) : (
                <>
                  <ListItem disablePadding>
                    {groupExpanded ? (
                      <ListItemButton
                        onClick={() => toggleNavGroup(entry.id)}
                        aria-expanded
                        aria-controls={groupListId}
                        sx={{ borderRadius: 2, px: 2, py: 0 }}
                      >
                        <ListItemText
                          primary={groupLabel}
                          primaryTypographyProps={groupLabelTypographyProps}
                        />
                      </ListItemButton>
                    ) : (
                      <ListItemButton
                        onClick={() => toggleNavGroup(entry.id)}
                        aria-expanded={false}
                        sx={{ ...navButtonSx(groupActive, false, variant), pr: 1 }}
                      >
                        <ListItemIcon>
                          <GroupIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={groupLabel}
                          primaryTypographyProps={
                            groupActive
                              ? { ...groupLabelTypographyProps, color: 'inherit' }
                              : groupLabelTypographyProps
                          }
                        />
                        <ExpandMoreIcon fontSize="small" sx={{ flexShrink: 0, ml: 0.5 }} />
                      </ListItemButton>
                    )}
                  </ListItem>
                  <li>
                    <Collapse in={groupExpanded} timeout="auto" unmountOnExit>
                      <List
                        id={groupListId}
                        disablePadding
                        sx={{ ml: 1.5, mb: 0.5, borderLeft: 1, borderColor: 'divider' }}
                      >
                        {entry.items.map((item) => (
                          <NavItemRow
                            key={item.path}
                            item={item}
                            variant={variant}
                            collapsed={false}
                            nested
                            onItemClick={onItemClick}
                          />
                        ))}
                      </List>
                    </Collapse>
                  </li>
                </>
              )}
            </Fragment>
          );
        }

        return (
          <Fragment key={entry.path}>
            {showDivider && <Divider component="li" sx={{ my: 1 }} />}
            <NavItemRow
              item={entry}
              variant={variant}
              collapsed={isCollapsed}
              onItemClick={onItemClick}
            />
          </Fragment>
        );
      })}
      {includeSettings && settingsVisible && (
        <>
          <Divider component="li" sx={{ my: 1 }} />
          <NavItemRow
            item={settingsNavItem}
            variant={variant}
            collapsed={isCollapsed}
            onItemClick={onItemClick}
          />
        </>
      )}
      {openGroup && flyout && (
        <Menu
          id={`nav-group-menu-${openGroup.id}`}
          anchorEl={flyout.anchor}
          open
          onClose={closeFlyout}
          disableScrollLock
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { minWidth: 200, ml: 1 } }}
        >
          {openGroup.items.map((item) => (
            <GroupMenuItem
              key={item.path}
              item={item}
              pathname={location.pathname}
              search={location.search}
              onNavigate={(path) => {
                navigateAwayFromWorkOrderDetail(location.pathname, path, navigate);
                onItemClick?.();
                closeFlyout();
              }}
            />
          ))}
        </Menu>
      )}
    </>
  );
}

function GroupMenuItem({
  item,
  pathname,
  search,
  onNavigate,
}: {
  item: NavItem;
  pathname: string;
  search: string;
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation('navigation');
  const active = itemIsActive(item, pathname, search);
  const Icon = item.icon;
  const label = t(item.labelKey);
  return (
    <MenuItem
      selected={active}
      onClick={() => onNavigate(item.path)}
      aria-current={active ? 'page' : undefined}
    >
      <ListItemIcon>
        <Icon fontSize="small" />
      </ListItemIcon>
      {label}
    </MenuItem>
  );
}

function navButtonSx(active: boolean, collapsed: boolean, variant: NavListVariant) {
  const rail = variant === 'rail';
  return {
    borderRadius: 2,
    mb: 0.5,
    justifyContent: collapsed ? 'center' : 'flex-start',
    px: collapsed ? 1 : 2,
    backgroundColor: active ? 'primary.main' : 'transparent',
    color: active ? 'primary.contrastText' : 'text.primary',
    '&:hover': {
      backgroundColor: active ? (rail ? 'primary.main' : 'primary.dark') : 'action.hover',
    },
    '& .MuiListItemIcon-root': {
      color: active
        ? rail
          ? 'common.white'
          : 'primary.contrastText'
        : rail
          ? darkSurfaces.mutedIcon
          : 'text.secondary',
      minWidth: collapsed ? 0 : NAV_ICON_SLOT,
      justifyContent: 'center',
    },
  };
}
